import { useState } from 'react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/public/v1/service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { ImageGallery, PublicImageWarning } from '@frontend/image-gallery';
import { ImageGalleryPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Heading, Modal, ModalControlModalProps } from '@frontend/design-system';

type AdditionalImageBuckets = 'brand';

interface Props {
  locationIds: string[];
  modalProps: ModalControlModalProps;
  onCancel?: () => void;
  onSelect: (file?: Media) => void;
  showLocationFilter?: boolean;
  additionalImageBuckets?: AdditionalImageBuckets[];
}

/**
 * A modal component for selecting images from an image gallery.
 *
 * Provides functionality to:
 * - Browse and select images from multiple locations
 * - Upload new images to the gallery
 * - Handle image selection with single-click and double-click interactions
 * - Display public image warnings to users
 *
 * @param props - The component props
 * @param props.locationIds - Array of location IDs to filter images by
 * @param props.modalProps - Modal control props for managing modal state
 * @param props.onCancel - Optional callback for custom cancel behavior. If not provided, uses modalProps.onClose
 * @param props.onSelect - Callback function called when an image is selected. Receives the selected Media object or undefined
 * @param props.showLocationFilter - Optional flag to show location filter when we don't want to show location filter
 *
 * @example
 * ```tsx
 * const { modalProps } = useModalControl();
 *
 * <SelectImageModal
 *   locationIds={['location-1', 'location-2']}
 *   modalProps={modalProps}
 *   onCancel={() => console.log('Do not close entire modal with multi-view modals')}
 *   onSelect={(media) => {
 *     console.log('Selected image:', media);
 *     modalProps.onClose();
 *   }}
 * />
 * ```
 */

export const SelectImageModal = ({
  locationIds,
  modalProps,
  onCancel,
  onSelect,
  showLocationFilter,
  additionalImageBuckets,
}: Props) => {
  const { t } = useTranslation('content-composer');
  const [selectedImage, setSelectedImage] = useState<Media>();

  const handleSelect = (media?: Media) => {
    onSelect(media);
    setSelectedImage(undefined);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    modalProps.onClose();
  };

  return (
    <>
      <Modal.Header>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(3),
          }}
        >
          <Heading level={2}>{t('Upload or Select Image')}</Heading>
          <Icon name='x-small' color='light' size={16} onClick={modalProps.onClose} />
        </div>
      </Modal.Header>
      <Modal.Body>
        <PublicImageWarning />
        <ImageGallery
          locationIds={locationIds}
          handleImageSelect={setSelectedImage}
          onDoubleClick={handleSelect}
          selectedImages={[selectedImage]}
          css={{ '.image-gallery-content': { marginTop: theme.spacing(2) } }}
          showLocationFilter={showLocationFilter}
          trackingId={ImageGalleryPrefixes.ContentComposer}
          additionalGalleries={additionalImageBuckets}
        />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Select'),
          onClick: () => handleSelect(selectedImage),
          disabled: !selectedImage,
        }}
        secondary={{ label: t('Cancel'), onClick: handleCancel }}
      />
    </>
  );
};
